import { useDateFormatter } from '@/hooks';
import { useOrganizations } from '@/pages/Organizations/hooks/useOrganizations';
import StudyContactCell, { type StudyContactCellProps } from '@/pages/Studies/components/StudyContactCell';
import globalStyles from '@/styles/index.module.css';
import type { UserStudyPublic } from '@/types';
import { FullLoading } from '@components/Loaders';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useForm } from '@tanstack/react-form';
import { getRouteApi } from '@tanstack/react-router';
import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import clsx from 'clsx';
import React, { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserActionRow } from './UserActionRow';
import styles from './UserDetails.module.css';
import { UserHeaderCard, type UserEditValues } from './UserHeaderCard';
import { useUserDetail, useUserStudies } from './useUserDetail';

type StudyContactValue = StudyContactCellProps['value'];

// getRouteApi avoids a circular import (UserDetails/index → $userId.lazy → UserDetails/index)
const routeApi = getRouteApi('/users/$userId');

const UserDetails: FC = () => {
  const { userId } = routeApi.useParams();
  const { t: tUsers } = useTranslation('users');

  const { data: user, isPending } = useUserDetail(userId);
  const { data: orgs = [] } = useOrganizations();
  const { data: studies = [], isPending: isStudiesPending } = useUserStudies(userId);
  const formatDate = useDateFormatter();

  const [isEditing, setIsEditing] = useState(false);

  const form = useForm({
    defaultValues: {
      first_name: user?.full_name?.split(' ')[0] ?? '',
      last_name: user?.full_name?.split(' ').slice(1).join(' ') ?? '',
      email: user?.email ?? '',
      is_active: user?.is_active ?? true,
    } satisfies UserEditValues,
    // TODO: implement onSubmit when PATCH /users/{userId} endpoint lands
    onSubmit: async () => {
      setIsEditing(false);
    },
  });

  useEffect(() => {
    if (!user || isEditing) return;
    form.reset({
      first_name: user.full_name?.split(' ')[0] ?? '',
      last_name: user.full_name?.split(' ').slice(1).join(' ') ?? '',
      email: user.email ?? '',
      is_active: user.is_active ?? true,
    });
  }, [user, isEditing, form]);

  const handleEdit = useCallback(() => setIsEditing(true), []);

  const handleCancel = useCallback(() => {
    form.reset({
      first_name: user?.full_name?.split(' ')[0] ?? '',
      last_name: user?.full_name?.split(' ').slice(1).join(' ') ?? '',
      email: user?.email ?? '',
      is_active: user?.is_active ?? true,
    });
    setIsEditing(false);
  }, [form, user]);

  const handleSave = useCallback(() => {
    void form.handleSubmit();
  }, [form]);

  const studyColDefs = useMemo<ColDef<UserStudyPublic>[]>(
    () => [
      { field: 'name', headerName: tUsers('study_col_name') },
      { field: 'org_name', headerName: tUsers('study_col_organization') },
      {
        colId: 'contact',
        headerName: tUsers('study_col_contact'),
        valueGetter: (params: ValueGetterParams<UserStudyPublic>): StudyContactValue => {
          const contact = params.data?.contact;
          if (!contact) return null;
          const name = `${contact.first_name} ${contact.last_name}`.trim();
          return { name: name.length > 0 ? name : null, email: contact.email };
        },
        cellRenderer: (params: { data: UserStudyPublic; value: StudyContactValue }) => (
          <StudyContactCell id={params.data.id} value={params.value} />
        ),
      },
      {
        field: 'permissions',
        headerName: tUsers('study_col_permissions'),
        valueFormatter: (params: ValueFormatterParams<UserStudyPublic, string[]>) => (params.value ?? []).join(', '),
      },
      {
        field: 'created_at',
        headerName: tUsers('study_col_created_at'),
        valueFormatter: (params: ValueFormatterParams<UserStudyPublic, string>) => formatDate(params.value),
      },
      { field: 'created_by_full_name', headerName: tUsers('study_col_created_by') },
    ],
    [tUsers, formatDate],
  );

  const orgNameMap = useMemo(() => new Map(orgs.map(o => [o.id, o.name])), [orgs]);

  if (isPending) return <FullLoading label={tUsers('detail_loading')} />;
  if (!user) return <Text>{tUsers('detail_not_found')}</Text>;

  const primaryOrgId = Object.keys(user.allowed_roles ?? {})[0];

  const orgMemberships = Object.entries(user.allowed_roles ?? {}).map(([oId, roles]) => ({
    orgId: oId,
    roles,
    isPrimary: oId === primaryOrgId,
  }));

  return (
    <Stack fullHeight fullWidth orientation="vertical">
      <UserActionRow
        isEditing={isEditing}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onSave={handleSave}
        user={user}
      />

      <Stack className={clsx(styles.content, globalStyles.scrollbars)} gap={4} orientation="vertical">
        <UserHeaderCard form={form} isEditing={isEditing} user={user} />

        {/* Organization Memberships */}
        <Stack className={styles.section} gap={2} orientation="vertical">
          <Text as="h3">{tUsers('org_memberships')}</Text>
          {orgMemberships.map(({ orgId: oId, roles, isPrimary }) => (
            <Stack alignItems="center" gap={4} key={oId} orientation="horizontal">
              <Text as="span">{orgNameMap.get(oId) ?? oId}</Text>
              {roles.map(role => (
                <Badge key={role} size="xs" variant="default">
                  {role}
                </Badge>
              ))}
              {isPrimary && <Text as="span">{tUsers('primary_role_indicator')}</Text>}
            </Stack>
          ))}
        </Stack>

        {/* Studies the user has access to */}
        <Stack className={styles.section} gap={2} orientation="vertical">
          <Text as="h3">{tUsers('study_count', { count: studies.length })}</Text>
          <Table<UserStudyPublic>
            columnDefs={studyColDefs}
            domLayout="autoHeight"
            loading={isStudiesPending}
            rowData={studies}
            tableId="user-studies-table"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default UserDetails;
